/* sugar:assets/product-media-gallery.js@2.1.8 */
/* Product media gallery engine (shared by the Product Media family).
 *
 * One init per .product-media-gallery root. Configuration rides on the root's
 * data attributes; thumbnail configuration comes from the Gallery Thumbnails
 * config child's [data-gt] marker inside the same [data-pmg-host] wrapper (no
 * marker = no thumbnails). Arrows and pagination are the shared slider chrome
 * (window.SugarSliderChrome); video overlays are the video-controls engine,
 * which re-scans on the `gallery:init` event this file dispatches after every
 * (re)initialization and after variant filtering rebuilds the slide lists.
 */
(function () {
  'use strict';

  if (window.__pmgEngine) return;
  window.__pmgEngine = true;

  /* The slides after the first stay out of the layout (see the gallery stylesheet) until
     the first image has landed, so nothing competes with it for the connection. `now`
     stops the wait: the visitor has asked for another slide. */
  function markLanded(galleryEl, now) {
    if (!galleryEl || galleryEl.hasAttribute('data-pmg-landed')) return;
    var done = function () {
      if (galleryEl.hasAttribute('data-pmg-landed')) return;
      galleryEl.setAttribute('data-pmg-landed', '');
      (galleryEl.__pmgOnLanded || []).forEach(function (fn) { fn(); });
      galleryEl.__pmgOnLanded = null;
    };
    if (now) return done();
    if (galleryEl.__pmgLanding) return;
    var hero = galleryEl.querySelector('[data-pmg-hero] img');
    if (!hero || hero.complete) return done();
    galleryEl.__pmgLanding = true;
    hero.addEventListener('load', done, { once: true });
    hero.addEventListener('error', done, { once: true });
    setTimeout(done, 4000);
  }

  function whenLanded(galleryEl, fn) {
    if (galleryEl.hasAttribute('data-pmg-landed')) return fn();
    (galleryEl.__pmgOnLanded = galleryEl.__pmgOnLanded || []).push(fn);
  }

  /* Every slide a slider can show, in order. With "Show only variant media" the page arrives
     showing the selected variant's slides; the others wait in a template at the end of the
     gallery, with a marker where each rendered slide sits. */
  function allSlides(sliderEl, part) {
    var live = Array.from(sliderEl.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
    var root = sliderEl.closest('.product-media-gallery');
    var rest = root && root.querySelector(':scope > template[data-pmg-rest="' + part + '"]');
    if (!rest) return live;
    var all = [];
    var next = 0;
    Array.from(rest.content.children).forEach(function (node) {
      if (node.hasAttribute('data-pmg-live')) {
        if (live[next]) all.push(live[next]);
        next++;
      } else {
        all.push(node);
      }
    });
    return all;
  }

  // The thumbnail rail stays still while the thumbnail being made active is fully in view, and
  // otherwise moves only far enough to bring it in at the near end. Splide reads `focus` (the
  // slot the active slide sits in) on each move, so it is set to the slot the thumbnail already
  // holds. A looping rail keeps Splide's own behaviour.
  function holdThumbRail(nav, vertical) {
    if (!nav || nav.is('loop') || nav.__pmgHold) return;
    nav.__pmgHold = true;
    var go = nav.go.bind(nav);
    nav.go = function (control) {
      if (typeof control === 'number') {
        var track = nav.Components.Elements.track;
        var cs = getComputedStyle(track);
        var r = track.getBoundingClientRect();
        var lo = vertical ? r.top + parseFloat(cs.paddingTop) : r.left + parseFloat(cs.paddingLeft);
        var hi = vertical ? r.bottom - parseFloat(cs.paddingBottom) : r.right - parseFloat(cs.paddingRight);
        var first = -1;
        var last = -1;
        nav.Components.Slides.get(true).forEach(function (s) {
          var b = s.slide.getBoundingClientRect();
          var a = vertical ? b.top : b.left;
          var z = vertical ? b.bottom : b.right;
          if (a >= lo - 1 && z <= hi + 1) {
            if (first < 0) first = s.index;
            last = s.index;
          }
        });
        if (first > -1) nav.options.focus = control < first ? 0 : control > last ? last - first : control - first;
      }
      return go(control);
    };
  }

  function initGallery(container) {
    markLanded(container.querySelector('.product-media-gallery__main'));
    if (typeof Splide === 'undefined') {
      setTimeout(function () {
        initGallery(container);
      }, 100);
      return;
    }

    var sectionId = container.dataset.sectionId;
    var galleryEl = container.querySelector('.product-media-gallery__main');
    var thumbsEl = container.querySelector('.product-media-gallery__thumbs');
    if (!galleryEl) return;

    // Outside banner rows above and below the slides, and the row of cards above them on phones,
    // belong to the main column, so a thumbnail column beside it spans them too, as it does
    // before the slider mounts. On desktop the cards' row floats over the photo and takes no room.
    function bannerRowsHeight() {
      var h = 0;
      galleryEl.querySelectorAll(':scope > .product-media-gallery__stage > .product-media-gallery__ob').forEach(function (row) {
        if (row.dataset.ob === 'above' && getComputedStyle(row).position === 'absolute') return;
        h += row.offsetHeight;
      });
      return h;
    }

    var ds = container.dataset;
    var enableInfiniteScroll = ds.enableInfiniteScroll === 'true';
    var carouselSpacing = parseInt(ds.carouselSpacing, 10) || 0;
    var animationSpeed = parseInt(ds.animationSpeed, 10) || 500;
    var desktopLayout = ds.desktopLayout || 'carousel';
    var mobileCarouselWidth = ds.mobileCarouselWidth || 'full_width';
    var mobileNextSlidePreview = ds.mobileNextSlidePreview === 'true';
    var mediaAspectRatio = ds.mediaAspectRatio || 'original';

    // Variant settings
    var variantImageOnLoad = ds.variantImageOnLoad === 'true';
    var navigateToVariantImage = ds.navigateToVariantImage === 'true';
    var scrollToGalleryOnVariant = ds.scrollToGalleryOnVariant === 'true';
    var showOnlyVariantMedia = ds.showOnlyVariantMedia === 'true';
    var variantOptionName = ds.variantOptionName || 'Color';
    var showGlobalMedia = ds.showGlobalMedia !== 'false';
    var productTitle = (ds.productTitle || '').toLowerCase();
    var productOptions = [];
    try {
      productOptions = ds.productOptions ? JSON.parse(ds.productOptions) : [];
    } catch (e) {
      productOptions = [];
    }
    // Every value of the filtering option, lower case (written only with Show only variant media)
    var optionValuesLower = [];
    try {
      optionValuesLower = ds.variantOptionValues ? JSON.parse(ds.variantOptionValues) : [];
    } catch (e) {
      optionValuesLower = [];
    }

    // Thumbnail configuration comes from the Gallery Thumbnails child marker
    var host = container.closest('[data-pmg-host]');
    var gt = host ? host.querySelector('[data-gt]') : null;
    var gtd = gt ? gt.dataset : {};
    var desktopThumbsPos = gt ? gtd.dPos || 'bottom' : 'hidden';
    var desktopThumbsCount = parseInt(gtd.dCount, 10) || 5;
    // A bottom strip at a fixed thumbnail width (Thumbnail size: Fixed width), and the count a
    // tablet (750-1023px) shows instead of the desktop one
    var desktopFixedWidth = gtd.dSize === 'width';
    var tabletThumbsCount = parseInt(gtd.tCount, 10) || 0;
    var desktopThumbWidth = parseInt(gtd.dWidth, 10) || 80;
    var desktopThumbArrows = gt ? gtd.dArrows !== undefined : false;
    var mobileThumbsPos = gt ? gtd.mPos || 'hidden' : 'hidden';
    var mobileThumbsCount = parseInt(gtd.mCount, 10) || 4;
    var mobileThumbWidth = parseInt(gtd.mWidth, 10) || 60;
    var mobileThumbArrows = gt ? gtd.mArrows !== undefined : false;
    var thumbnailSpacing = parseInt(gtd.spacing, 10);
    if (isNaN(thumbnailSpacing)) thumbnailSpacing = 10;
    var thumbnailAspectRatio = gtd.thumbAspect || 'square';
    // Arrows beside a vertical strip take their size plus 6px at each end of the track (the
    // gallery sheet's margin); arrows over the strip ends take none.
    var thumbArrowRoom = gtd.arrowsOver !== undefined ? 0 : ((parseInt(gtd.arrowSize, 10) || 22) + 6) * 2;

    // Theme editor: the thumbnail strip is drawn by the gallery host, so a
    // click on it would select the host block. Move the Gallery Thumbnails
    // child's editor attribute onto the strip so clicking a thumbnail opens
    // that child's settings instead. It moves rather than copies: the empty
    // marker prints ahead of the gallery, and the strip stays the one element
    // the editor outlines and scrolls to when the child is selected. Outside
    // the editor the attribute is absent and this is a no-op.
    if (gt && thumbsEl) {
      var editorBlock = gt.getAttribute('data-shopify-editor-block');
      if (editorBlock) {
        thumbsEl.setAttribute('data-shopify-editor-block', editorBlock);
        gt.removeAttribute('data-shopify-editor-block');
      }
    }

    // Hand-built Gallery block: adopt slides rendered by Gallery Item children
    // at the wrapper level into the track (idempotent — adopted slides are no
    // longer direct children of the host).
    if (ds.slidesFromHost === 'true' && host) {
      var adoptList = galleryEl.querySelector('.splide__list');
      if (adoptList) {
        host.querySelectorAll(':scope > li[data-gallery-slide]').forEach(function (li) {
          adoptList.appendChild(li);
        });
      }
    }

    // Runtime loop flag (Gallery Item videos render without a loop attribute)
    if (ds.videoLoop !== undefined) {
      galleryEl.querySelectorAll('.product-media-gallery__video').forEach(function (v) {
        v.loop = ds.videoLoop === 'true';
      });
    }

    // Auto-build thumbnails from the slides when Liquid could not render them
    if (thumbsEl && thumbsEl.hasAttribute('data-thumbs-auto')) {
      var autoList = thumbsEl.querySelector('.splide__list');
      if (autoList && !autoList.children.length) {
        galleryEl.querySelectorAll('.splide__list > li.splide__slide').forEach(function (slide) {
          var li = document.createElement('li');
          li.className = 'splide__slide';
          var img = slide.querySelector('img');
          var video = slide.querySelector('video');
          var src = img ? img.currentSrc || img.src : video ? video.getAttribute('poster') : '';
          if (src) {
            var thumbImg = document.createElement('img');
            // Lazy before the source is set: WebKit starts the download at `src` otherwise
            thumbImg.loading = 'lazy';
            // A square thumbnail is cropped to its box, so it can take a small file instead of
            // the slide's full-size one (an "original" one keeps it: its height follows the file)
            if (thumbnailAspectRatio === 'square' && img && img.getAttribute('srcset')) {
              thumbImg.sizes = '(min-width: 750px) 220px, 110px';
              thumbImg.srcset = img.getAttribute('srcset');
            }
            thumbImg.src = src;
            thumbImg.alt = img ? img.alt || '' : '';
            thumbImg.className = 'product-media-gallery__thumb-image';
            li.appendChild(thumbImg);
          }
          if (video) {
            var play = document.createElement('span');
            play.className = 'product-media-gallery__thumb-play';
            play.innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="m380-300 280-180-280-180v360ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>';
            li.appendChild(play);
          }
          autoList.appendChild(li);
        });
      }
    }

    var isMobile = window.innerWidth < 750;
    var useTabletCount = !isMobile && window.innerWidth < 1024 && tabletThumbsCount > 0;
    var thumbsPos = isMobile ? mobileThumbsPos : desktopThumbsPos;
    var thumbsCount = isMobile ? mobileThumbsCount : useTabletCount ? tabletThumbsCount : desktopThumbsCount;
    var thumbWidth = isMobile ? mobileThumbWidth : desktopThumbWidth;
    var thumbArrows = isMobile ? mobileThumbArrows : desktopThumbArrows;
    var isVertical = thumbsPos === 'left' || thumbsPos === 'right';

    var useStaticLayout = !isMobile && (desktopLayout === 'stacked' || desktopLayout === 'grid');
    // Counted before the old instance is destroyed, so a looping one's copies are skipped.
    var slideCount = galleryEl.querySelectorAll('.splide__slide:not(.splide__slide--clone)').length;

    // Destroy existing instances (theme editor / resize re-init)
    if (galleryEl.__verticalResizeObserver) {
      galleryEl.__verticalResizeObserver.disconnect();
      galleryEl.__verticalResizeObserver = null;
    }
    if (galleryEl.__pmgVideoResize) {
      galleryEl.__pmgVideoResize.disconnect();
      galleryEl.__pmgVideoResize = null;
    }
    if (galleryEl._scChrome) {
      galleryEl._scChrome.detach();
      galleryEl._scChrome = null;
    }
    if (thumbsEl && thumbsEl._scChrome) {
      thumbsEl._scChrome.detach();
      thumbsEl._scChrome = null;
    }
    if (galleryEl.splide) {
      galleryEl.splide.destroy();
    }
    if (thumbsEl && thumbsEl.splide) {
      thumbsEl.splide.destroy();
    }
    if (galleryEl.variantChangeUnsubscriber) {
      galleryEl.variantChangeUnsubscriber();
      galleryEl.variantChangeUnsubscriber = null;
    }

    // Destroying a Splide instance strips the track's inline style, so the
    // measured height has to be written again by whichever run is current.
    var initToken = (galleryEl.__pmgInitToken || 0) + 1;
    galleryEl.__pmgInitToken = initToken;
    galleryEl.__pmgHeightSync = null;

    var mainOptions = {
      // Stacked and grid show every slide at once, so there is nothing to loop to: a loop
      // would put copies of the whole gallery before and after it, all of them on screen.
      type: enableInfiniteScroll && !useStaticLayout ? 'loop' : 'slide',
      perPage: 1,
      arrows: false,
      pagination: false,
      gap: carouselSpacing,
      speed: animationSpeed,
      autoHeight: mediaAspectRatio === 'original',
      // A video's timeline (video-controls.js) scrubs; a touch that starts on it is not a swipe
      noDrag: 'input, textarea, .media-block__timeline, .pmg-no-drag',
    };

    var useMobilePreview = isMobile && mobileNextSlidePreview;
    if (useMobilePreview) {
      mainOptions.fixedWidth = '90%';
      mainOptions.trimSpace = true;
    }
    // Bleed right: the gallery sheet runs the track on to the right edge of the screen and pads
    // that strip back out. The slider is told the same padding (it writes its own over the
    // sheet's), so the slides keep the content's width, the first starts at its left edge and a
    // trimmed last move ends the last slide at its right edge. A thumbnail column on the right
    // holds that edge, so the gallery stays inside it there.
    if (isMobile && mobileCarouselWidth === 'bleed_right' && mobileThumbsPos !== 'right') {
      mainOptions.padding = { left: 0, right: 'calc(50vw - 50%)' };
    }

    // Desktop bleed right: the track runs on to the right edge of the screen while every slide
    // keeps the gallery's width, so the next photo peeks past the gallery and the trimmed last
    // move ends the last photo at the screen's edge with the one before it peeking on the left.
    // The track still clips on the gallery's left edge. Width changes re-run this whole init,
    // so one measurement per init is enough.
    if (!isMobile && ds.desktopBleed === 'right' && !useStaticLayout && desktopThumbsPos !== 'right') {
      var bleedSlideW = galleryEl.clientWidth;
      var bleed = Math.max(0, Math.round(document.documentElement.clientWidth - galleryEl.getBoundingClientRect().right));
      galleryEl.style.setProperty('--pmg-bleed', bleed + 'px');
      mainOptions.fixedWidth = bleedSlideW + 'px';
      mainOptions.trimSpace = true;
    }

    if (useStaticLayout) {
      mainOptions.perPage = slideCount;
      mainOptions.drag = false;
      mainOptions.autoHeight = false;
    }

    // A popup printed inside a row above the slides (a Media Card's gift popup, say) brings its
    // own .splide__track, and Splide takes the first track it finds in the root, so the gallery
    // would never mount. The dialog goes where popup.js sends it on open anyway, the body; in the
    // editor it moves beside the gallery instead, where the editor still finds the block inside
    // its section. popup.js finds a dialog by its id wherever it lives.
    var designMode = window.Shopify && window.Shopify.designMode;
    Array.prototype.forEach.call(galleryEl.querySelectorAll('[data-popup-dialog]'), function (dialog) {
      (designMode ? container : document.body).appendChild(dialog);
    });

    var mainSplide = new Splide(galleryEl, mainOptions);

    function attachChrome() {
      if (!window.SugarSliderChrome || useStaticLayout) return;
      galleryEl._scChrome = window.SugarSliderChrome.attach(mainSplide, galleryEl, { dotMode: 'slide' });
      if (thumbsEl && thumbsEl.splide) {
        thumbsEl._scChrome = window.SugarSliderChrome.attach(thumbsEl.splide, thumbsEl, { dotMode: 'slide' });
      }
    }

    // Thumbnails (skip for static layouts on desktop)
    if (thumbsEl && thumbsPos !== 'hidden' && !useStaticLayout) {
      var thumbGap = thumbnailSpacing;

      if (isVertical) {
        // Vertical layout (left/right): fixedWidth, perPage from measured height
        mainSplide.mount();
        galleryEl.splide = mainSplide;

        var mainTrack = galleryEl.querySelector('.splide__track');
        var galleryHeight = (mainTrack ? mainTrack.offsetHeight : 400) + bannerRowsHeight();
        var arrowSpace = thumbArrows ? thumbArrowRoom : 0;
        var trackHeight = galleryHeight - arrowSpace;

        thumbsEl.style.height = galleryHeight + 'px';
        container.style.setProperty('--gallery-height', galleryHeight + 'px');

        var estimatedThumbHeight = thumbWidth;
        if (thumbnailAspectRatio === 'original') {
          var firstThumbImg = thumbsEl.querySelector('.product-media-gallery__thumb-image');
          if (firstThumbImg && firstThumbImg.naturalWidth > 0 && firstThumbImg.naturalHeight > 0) {
            estimatedThumbHeight = Math.round((thumbWidth * firstThumbImg.naturalHeight) / firstThumbImg.naturalWidth);
          }
        }
        var visibleThumbs = Math.max(Math.floor(trackHeight / (estimatedThumbHeight + thumbGap)), 1);
        // Loops with a looping gallery once it holds more thumbnails than fit, as the bottom strip does
        var thumbCountV = thumbsEl.querySelectorAll('.splide__list > .splide__slide').length;

        var thumbsSplide = new Splide(thumbsEl, {
          type: enableInfiniteScroll && thumbCountV > visibleThumbs ? 'loop' : 'slide',
          direction: 'ttb',
          height: trackHeight,
          fixedWidth: thumbWidth,
          fixedHeight: estimatedThumbHeight,
          gap: thumbGap,
          perPage: visibleThumbs,
          pagination: false,
          arrows: false,
          isNavigation: true,
        });
        thumbsSplide.sync(mainSplide);
        thumbsSplide.mount();
        holdThumbRail(thumbsSplide, true);
        thumbsEl.splide = thumbsSplide;

        if (thumbnailAspectRatio === 'original' && estimatedThumbHeight === thumbWidth) {
          var firstImg = thumbsEl.querySelector('.product-media-gallery__thumb-image');
          if (firstImg && !firstImg.complete) {
            firstImg.addEventListener('load', function onLoad() {
              firstImg.removeEventListener('load', onLoad);
              if (firstImg.naturalWidth > 0 && firstImg.naturalHeight > 0) {
                var correctedHeight = Math.round((thumbWidth * firstImg.naturalHeight) / firstImg.naturalWidth);
                var correctedPerPage = Math.max(Math.floor(trackHeight / (correctedHeight + thumbGap)), 1);
                thumbsSplide.options = { fixedHeight: correctedHeight, perPage: correctedPerPage };
                thumbsSplide.refresh();
              }
            });
          }
        }

        // Sync vertical layout heights after layout settles
        (function () {
          function syncVerticalHeight() {
            if (galleryEl.__pmgInitToken !== initToken) return;
            if (!thumbsEl.splide) return;
            var track = galleryEl.querySelector('.splide__track');
            if (!track) return;

            var newHeight;
            if (mediaAspectRatio === 'original') {
              try {
                var cs = mainSplide.Components.Slides.getAt(mainSplide.index);
                if (cs && cs.slide) {
                  var media = cs.slide.querySelector(
                    '.product-media-gallery__image, .product-media-gallery__video, .product-media-gallery__deferred-media'
                  );
                  if (media && media.offsetHeight > 0) newHeight = media.offsetHeight;
                }
              } catch (e) {}
            } else {
              var firstSlide = galleryEl.querySelector('.splide__slide');
              if (firstSlide && firstSlide.offsetHeight > 0) newHeight = firstSlide.offsetHeight;
            }
            if (!newHeight || newHeight <= 0) return;

            var columnHeight = newHeight + bannerRowsHeight();
            var currentH = parseInt(thumbsEl.style.height) || 0;
            if (Math.abs(columnHeight - currentH) < 2) return;

            track.style.height = newHeight + 'px';
            thumbsEl.style.height = columnHeight + 'px';
            container.style.setProperty('--gallery-height', columnHeight + 'px');

            var space = thumbArrows ? thumbArrowRoom : 0;
            var newTrackHeight = columnHeight - space;
            var estH = thumbWidth;
            if (thumbnailAspectRatio === 'original') {
              var tImg = thumbsEl.querySelector('.product-media-gallery__thumb-image');
              if (tImg && tImg.naturalWidth > 0 && tImg.naturalHeight > 0) {
                estH = Math.round((thumbWidth * tImg.naturalHeight) / tImg.naturalWidth);
              }
            }
            var pp = Math.max(Math.floor(newTrackHeight / (estH + thumbGap)), 1);
            try {
              thumbsEl.splide.options = { height: newTrackHeight, perPage: pp };
              thumbsEl.splide.refresh();
            } catch (e) {}
          }

          [50, 200, 500].forEach(function (d) {
            setTimeout(syncVerticalHeight, d);
          });

          if (typeof ResizeObserver !== 'undefined') {
            var lastW = galleryEl.offsetWidth;
            galleryEl.__verticalResizeObserver = new ResizeObserver(function () {
              var w = galleryEl.offsetWidth;
              if (Math.abs(w - lastW) < 1) return;
              lastW = w;
              setTimeout(syncVerticalHeight, 50);
            });
            galleryEl.__verticalResizeObserver.observe(galleryEl);
          }
        })();
      } else {
        // Horizontal layout (bottom): perPage only, Splide calculates widths
        var thumbsOptionsH = {
          gap: thumbGap,
          pagination: false,
          arrows: false,
          isNavigation: true,
          perPage: thumbsCount,
        };
        // Fade under the arrows: the room the gallery sheet keeps beside the arrows is the
        // track's padding rather than its margin, so the thumbnails run on under them. The
        // slider writes its own padding over the sheet's, so it is told the same (size + 8px).
        if (thumbArrows && gtd.arrowsFade !== undefined && gtd.arrowsOver === undefined) {
          var thumbFadePad = (parseInt(gtd.arrowSize, 10) || 22) + 8;
          thumbsOptionsH.padding = { left: thumbFadePad, right: thumbFadePad };
        }
        // Fixed width: every thumbnail keeps its width, and the count that fits drives the moves
        if (!isMobile && !useTabletCount && desktopFixedWidth) {
          var fixedThumbTrack = thumbsEl.querySelector('.splide__track');
          var fixedTrackW = fixedThumbTrack ? fixedThumbTrack.clientWidth : thumbsEl.clientWidth;
          if (thumbsOptionsH.padding) fixedTrackW -= thumbsOptionsH.padding.left + thumbsOptionsH.padding.right;
          thumbsOptionsH.fixedWidth = desktopThumbWidth;
          thumbsOptionsH.perPage = Math.max(1, Math.floor((fixedTrackW + thumbGap) / (desktopThumbWidth + thumbGap)));
        }
        // A looping gallery's strip loops too, once it holds more thumbnails than it shows, so it
        // carries on past the last picture the way the gallery does instead of stopping at its end.
        var thumbSlideCount = thumbsEl.querySelectorAll('.splide__list > .splide__slide').length;
        if (enableInfiniteScroll && thumbSlideCount > thumbsOptionsH.perPage) thumbsOptionsH.type = 'loop';
        var thumbsSplideH = new Splide(thumbsEl, thumbsOptionsH);
        mainSplide.sync(thumbsSplideH);
        mainSplide.mount();
        thumbsSplideH.mount();
        holdThumbRail(thumbsSplideH, false);

        galleryEl.splide = mainSplide;
        thumbsEl.splide = thumbsSplideH;
      }
    } else {
      mainSplide.mount();
      galleryEl.splide = mainSplide;
    }

    // Native video controls: a touch that starts in the control strip along a video's bottom
    // edge (the scrubber; on iOS the inline bar is about 48px tall) is a seek, not a swipe.
    // Splide reads its pointer-down on the track in the capture phase, so this runs one level
    // up, also capturing, and flags the video; the noDrag rule above then skips the drag while
    // the event still reaches the native controls. The rest of the picture swipes as before.
    function guardVideoControls() {
      if (galleryEl.__pmgGuard) return;
      galleryEl.__pmgGuard = true;
      ['touchstart', 'mousedown', 'pointerdown'].forEach(function (type) {
        galleryEl.addEventListener(
          type,
          function (e) {
            var video = e.target && e.target.closest ? e.target.closest('video') : null;
            if (!video) return;
            var point = e.touches && e.touches[0] ? e.touches[0] : e;
            var r = video.getBoundingClientRect();
            var inStrip = video.controls && point.clientY >= r.bottom - Math.min(72, r.height * 0.35);
            video.classList.toggle('pmg-no-drag', inStrip);
          },
          { capture: true, passive: true }
        );
      });
    }
    guardVideoControls();

    attachChrome();

    // Preload only the current and adjacent slides
    function preloadNearbySlides(index) {
      if (useStaticLayout) return;

      var slides = mainSplide.Components.Slides.get(true);
      var slideTotal = slides.length;
      if (!slideTotal) return;

      var seen = {};
      [index - 1, index, index + 1].forEach(function (targetIndex) {
        var normalizedIndex = targetIndex;
        if (enableInfiniteScroll) {
          normalizedIndex = ((targetIndex % slideTotal) + slideTotal) % slideTotal;
        } else if (targetIndex < 0 || targetIndex >= slideTotal) {
          return;
        }

        if (seen[normalizedIndex]) return;
        seen[normalizedIndex] = true;

        var slideItem = slides[normalizedIndex];
        if (slideItem && slideItem.slide) {
          slideItem.slide.querySelectorAll('img[loading="lazy" i]').forEach(function (img) {
            img.loading = 'eager';
            // A <picture> picks its file by screen width: eager loads that one, and a copy built
            // from the <img> alone would also fetch the other screen's file.
            if (img.complete || img.parentElement.localName === 'picture') return;
            var srcset = img.getAttribute('srcset');
            var src = img.getAttribute('src');
            if (!srcset && !src) return;
            var p = new Image();
            if (srcset) {
              p.sizes = img.getAttribute('sizes') || '';
              p.srcset = srcset;
            }
            if (src) p.src = src;
          });
        }

        if (!thumbsEl) return;
        var thumbSlides = thumbsEl.querySelectorAll('.splide__slide');
        var thumbSlide = thumbSlides[normalizedIndex];
        if (!thumbSlide) return;
        thumbSlide.querySelectorAll('img[loading="lazy" i]').forEach(function (img) {
          img.loading = 'eager';
          // As above: a <picture> loads its own file once eager.
          if (img.complete || img.parentElement.localName === 'picture') return;
          var srcset = img.getAttribute('srcset');
          var src = img.getAttribute('src');
          if (!srcset && !src) return;
          var p = new Image();
          if (srcset) {
            p.sizes = img.getAttribute('sizes') || '';
            p.srcset = srcset;
          }
          if (src) p.src = src;
        });
      });
    }

    whenLanded(galleryEl, function () { preloadNearbySlides(mainSplide.index); });
    mainSplide.on('moved', preloadNearbySlides);

    // A looping slider puts copies of the last slides in front of the first. Until the first
    // image has landed they have no width (see the gallery stylesheet), so the row was lined
    // up without them; when they take their width the first slide moves right and a copy
    // would sit in view until the slider next measures. Line the row up again before that
    // frame is drawn (or, mid-slide, as soon as the slide ends).
    if (enableInfiniteScroll && !useStaticLayout) {
      var realignAfterSlide = false;
      var realign = function () {
        if (galleryEl.__pmgInitToken !== initToken) return;
        if (mainSplide.Components.Controller.isBusy()) {
          realignAfterSlide = true;
          return;
        }
        mainSplide.Components.Move.reposition();
      };
      mainSplide.on('moved', function () {
        if (!realignAfterSlide) return;
        realignAfterSlide = false;
        realign();
      });
      whenLanded(galleryEl, realign);
    }

    // A visitor who moves the gallery before the first image has landed wants another
    // picture now, and the slider can only line up slides that are in the layout: an arrow
    // tapped that early left the first picture in view with the dots and thumbnails on the
    // second, and a looping gallery slid to an empty space. Every move lands the gallery
    // first, and so does a touch on it, because a drag measures where it starts as the
    // finger goes down.
    if (!useStaticLayout && !galleryEl.hasAttribute('data-pmg-landed')) {
      var controller = mainSplide.Components.Controller;
      var go = controller.go;
      controller.go = function () {
        markLanded(galleryEl, true);
        return go.apply(controller, arguments);
      };
      if (!galleryEl.__pmgLandOnTouch) {
        galleryEl.__pmgLandOnTouch = function () { markLanded(galleryEl, true); };
        galleryEl.addEventListener('touchstart', galleryEl.__pmgLandOnTouch, { capture: true, passive: true });
        galleryEl.addEventListener('mousedown', galleryEl.__pmgLandOnTouch, { capture: true, passive: true });
      }
    }

    // Track height management for fixed aspect ratios
    if (!useStaticLayout && mediaAspectRatio !== 'original') {
      var updateTrackHeightForAspectRatio = function () {
        var track = galleryEl.querySelector('.splide__track');
        var firstSlide = galleryEl.querySelector('.splide__slide');
        if (track && firstSlide) {
          var slideHeight = firstSlide.offsetHeight;
          if (slideHeight > 0) {
            track.style.height = slideHeight + 'px';
          }
        }
      };
      [0, 50, 200].forEach(function (delay) {
        setTimeout(function () {
          if (galleryEl.__pmgInitToken !== initToken) return;
          updateTrackHeightForAspectRatio();
        }, delay);
      });
      mainSplide.on('resized', updateTrackHeightForAspectRatio);
    }

    // Adaptive height — carousel layouts with original aspect ratio
    if (!useStaticLayout && mediaAspectRatio === 'original') {
      var scheduleGalleryHeightUpdate = function (targetIndex) {
        [0, 80, 180].forEach(function (delay) {
          setTimeout(function () {
            if (galleryEl.__pmgInitToken !== initToken) return;
            updateGalleryHeight(targetIndex);
          }, delay);
        });
      };

      galleryEl.__pmgHeightSync = scheduleGalleryHeightUpdate;

      var bindMediaHeightSync = function () {
        var mediaNodes = galleryEl.querySelectorAll(
          '.product-media-gallery__image, .product-media-gallery__video, .product-media-gallery__poster-image'
        );
        mediaNodes.forEach(function (mediaNode) {
          var isVideo = mediaNode.tagName === 'VIDEO';
          var ready = isVideo ? mediaNode.readyState > 0 : mediaNode.complete;
          if (!mediaNode.__heightSyncBound) {
            mediaNode.__heightSyncBound = true;
            mediaNode.addEventListener(isVideo ? 'loadedmetadata' : 'load', function () {
              if (galleryEl.__pmgHeightSync) galleryEl.__pmgHeightSync();
            });
          }
          if (ready) {
            scheduleGalleryHeightUpdate();
          }
        });
      };

      var updateThumbsHeight = function (newGalleryHeight) {
        if (!thumbsEl || !thumbsEl.splide || !isVertical) return;
        newGalleryHeight += bannerRowsHeight();
        var space = thumbArrows ? thumbArrowRoom : 0;
        var newTrackHeight = newGalleryHeight - space;

        var estimatedThumbHeight = thumbWidth;
        if (thumbnailAspectRatio === 'original') {
          var firstThumbImg = thumbsEl.querySelector('.product-media-gallery__thumb-image');
          if (firstThumbImg && firstThumbImg.naturalWidth > 0 && firstThumbImg.naturalHeight > 0) {
            estimatedThumbHeight = Math.round((thumbWidth * firstThumbImg.naturalHeight) / firstThumbImg.naturalWidth);
          }
        }
        var visibleThumbs = Math.max(Math.floor(newTrackHeight / (estimatedThumbHeight + thumbnailSpacing)), 1);

        // This runs several times on every slide change, while the strip is gliding to the new
        // thumbnail, so it never refreshes the strip: a refresh puts the strip straight onto its
        // end position and the glide becomes a snap. Nothing is touched when the column keeps
        // its height. A new height is set in place: the thumbnails have a fixed size and a strip
        // that follows the gallery can step to every thumbnail, so the count that fits changes
        // nothing in how it moves. A shorter or taller column can move the active thumbnail's
        // place (at the end of the strip), and the strip glides there once it has arrived.
        var strip = thumbsEl.splide;
        var opts = strip.options;
        if (opts.height === newTrackHeight && opts.perPage === visibleThumbs) return;

        thumbsEl.style.height = newGalleryHeight + 'px';
        container.style.setProperty('--gallery-height', newGalleryHeight + 'px');
        opts.height = newTrackHeight;
        opts.perPage = visibleThumbs;
        var thumbsTrack = thumbsEl.querySelector('.splide__track');
        if (thumbsTrack) thumbsTrack.style.height = newTrackHeight + 'px';

        var settle = function () {
          var move = strip.Components.Move;
          if (Math.abs(move.getPosition() - move.toPosition(strip.index, true)) < 1) return;
          strip.Components.Transition.start(strip.index, function () {});
        };
        var states = window.Splide && window.Splide.STATES;
        if (states && strip.state.is(states.MOVING)) {
          if (!strip.__pmgSettleBound) {
            strip.__pmgSettleBound = true;
            strip.on('moved', settle);
          }
          return;
        }
        settle();
      };

      var updateGalleryHeight = function (targetIndex) {
        var track = galleryEl.querySelector('.splide__track');
        if (!track) return;

        var index = typeof targetIndex === 'number' ? targetIndex : mainSplide.index;
        var currentSlide = mainSplide.Components.Slides.getAt(index);
        if (currentSlide && currentSlide.slide) {
          var media = currentSlide.slide.querySelector(
            '.product-media-gallery__image, .product-media-gallery__video, .product-media-gallery__deferred-media'
          );
          if (media) {
            var height = media.offsetHeight;
            if (height > 0) {
              track.style.height = height + 'px';
              updateThumbsHeight(height);
            }
          }
        }
      };

      bindMediaHeightSync();
      updateGalleryHeight();
      scheduleGalleryHeightUpdate();

      // A video is drawn at its poster's size until it plays, then at its own, which can be a
      // pixel or two different, and nothing fires when it switches. Pin the track again when
      // the video in view no longer matches it, or its bottom edge stays clipped.
      if (typeof ResizeObserver !== 'undefined' && galleryEl.querySelector('.product-media-gallery__video')) {
        galleryEl.__pmgVideoResize = new ResizeObserver(function () {
          if (galleryEl.__pmgInitToken !== initToken) return;
          var track = galleryEl.querySelector('.splide__track');
          var active = mainSplide.Components.Slides.getAt(mainSplide.index);
          var media = active && active.slide.querySelector(
            '.product-media-gallery__image, .product-media-gallery__video, .product-media-gallery__deferred-media'
          );
          if (!track || !media || media.tagName !== 'VIDEO' || !media.offsetHeight) return;
          if (track.style.height !== media.offsetHeight + 'px') updateGalleryHeight();
        });
        galleryEl.querySelectorAll('.product-media-gallery__video').forEach(function (video) {
          galleryEl.__pmgVideoResize.observe(video);
        });
      }

      mainSplide.on('move', function (newIndex) {
        scheduleGalleryHeightUpdate(newIndex);
      });
      // The slider clears the track's height whenever it re-measures, which it does when the
      // page's pictures finish loading. Unpinned, the track takes the tallest slide's height,
      // so it is pinned again before that frame is drawn, not a task later.
      mainSplide.on('resized', function () {
        updateGalleryHeight();
        scheduleGalleryHeightUpdate();
      });
      if (mobileCarouselWidth === 'full_width') {
        window.addEventListener(
          'load',
          function () {
            scheduleGalleryHeightUpdate();
          },
          { once: true }
        );
      }
    }

    // Pause all media when slide changes
    mainSplide.on('move', function () {
      if (typeof pauseAllMedia === 'function') {
        pauseAllMedia();
      }
    });

    // Variant media functionality
    (function () {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      // Read once per gallery: after a variant change the lists hold only that variant's slides,
      // and a re-init (resize) must still be able to bring the others back.
      if (!galleryEl.__pmgAllSlides) {
        galleryEl.__pmgAllSlides = {
          main: allSlides(galleryEl, 'main'),
          thumbs: thumbsEl ? allSlides(thumbsEl, 'thumbs') : [],
        };
      }
      var originalMainSlides = galleryEl.__pmgAllSlides.main;
      var originalThumbSlides = galleryEl.__pmgAllSlides.thumbs;

      function slideIds(slides) {
        return slides
          .map(function (slide) {
            return slide.dataset.mediaId;
          })
          .join(',');
      }

      function getVariantOptionValue(variant, optionName) {
        if (!variant || !productOptions.length) return null;
        var optionIndex = productOptions.findIndex(function (opt) {
          return opt.toLowerCase() === optionName.toLowerCase();
        });
        if (optionIndex === -1) return null;
        return variant.options ? variant.options[optionIndex] : null;
      }

      function writeVariantBadges(variant) {
        if (!variant) return;
        // An offer announces its variant without a title, only its options.
        var title = variant.title || (Array.isArray(variant.options) ? variant.options.join(' / ') : '');
        if (!title) return;
        var tags = galleryEl.querySelectorAll('[data-mb-variant]');
        for (var i = 0; i < tags.length; i++) {
          var sep = tags[i].getAttribute('data-mb-sep');
          tags[i].textContent = sep === null ? title : title.split(' / ').join(sep);
        }
      }

      function filterSlidesByVariant(variant) {
        writeVariantBadges(variant);
        if (!showOnlyVariantMedia) return;

        var optionValue = getVariantOptionValue(variant, variantOptionName);
        if (!optionValue) return;

        var optionValueLower = optionValue.toLowerCase();
        // A value that holds this one ("Brave Beige" holds "Beige") claims its own pictures, so
        // the alt text is read with those longer values taken out (the snippet reads it the same).
        var longerValues = optionValuesLower.filter(function (v) {
          return v !== optionValueLower && v.indexOf(optionValueLower) !== -1;
        });
        var featuredMediaId = variant.featured_media ? String(variant.featured_media.id) : '';
        var isFeaturedSlide = function (slide) {
          return (
            featuredMediaId &&
            String(slide.dataset.mediaId || '').slice(-featuredMediaId.length - 1) === '-' + featuredMediaId
          );
        };
        var matches = function (slide) {
          if (isFeaturedSlide(slide)) return true;
          var alt = (slide.dataset.mediaAlt || '').toLowerCase();
          var own = alt;
          for (var l = 0; l < longerValues.length; l++) own = own.split(longerValues[l]).join(' ');
          if (own.includes(optionValueLower)) return true;
          if (showGlobalMedia && (alt === '' || alt === productTitle)) return true;
          return false;
        };
        // The variant's own picture leads, as the page renders it on arrival, so an overlay set
        // to the first slide stays over it and the gallery opens on it rather than sliding there.
        var leadWithVariant = function (slides) {
          for (var i = 1; i < slides.length; i++) {
            if (isFeaturedSlide(slides[i])) return [slides[i]].concat(slides.slice(0, i), slides.slice(i + 1));
          }
          return slides;
        };

        var mainList = galleryEl.querySelector('.splide__list');
        var visibleSlides = leadWithVariant(originalMainSlides.filter(matches));
        if (visibleSlides.length === 0) visibleSlides = originalMainSlides;

        // Already showing these slides (the page arrived on this variant, or only another option
        // changed): rebuilding would redraw the same pictures.
        var shownSlides = mainList
          ? Array.from(mainList.querySelectorAll(':scope > .splide__slide:not(.splide__slide--clone)'))
          : [];
        if (slideIds(shownSlides) === slideIds(visibleSlides)) return;

        // The first image may not have landed yet, and the new first slide is not the one
        // it waits for: without this the gallery folds to nothing until then.
        markLanded(galleryEl, true);

        if (mainList) {
          mainList.innerHTML = '';
          visibleSlides.forEach(function (slide) {
            mainList.appendChild(slide.cloneNode(true));
          });
        }

        if (thumbsEl) {
          var thumbList = thumbsEl.querySelector('.splide__list');
          if (thumbList) {
            thumbList.innerHTML = '';
            var visibleThumbs = leadWithVariant(originalThumbSlides.filter(matches));
            if (visibleThumbs.length === 0) visibleThumbs = originalThumbSlides;
            visibleThumbs.forEach(function (slide) {
              thumbList.appendChild(slide.cloneNode(true));
            });
          }
        }

        mainSplide.refresh();
        if (thumbsEl && thumbsEl.splide) {
          thumbsEl.splide.refresh();
        }

        // Rebuilt slides are fresh clones — let the video-controls engine and
        // other slide-bound scripts rebind.
        galleryEl.dispatchEvent(new CustomEvent('gallery:init'));
      }

      function navigateToVariantMedia(variant) {
        if (!navigateToVariantImage) return;
        if (!variant || !variant.featured_media) return;

        var mediaId = variant.featured_media.id;
        var targetSlide = galleryEl.querySelector(
          '.splide__slide:not(.splide__slide--clone)[data-media-id="' + sectionId + '-' + mediaId + '"]'
        );
        if (targetSlide) {
          var slides = galleryEl.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
          var targetIndex = Array.prototype.indexOf.call(slides, targetSlide);
          if (targetIndex !== -1 && targetIndex !== mainSplide.index) {
            mainSplide.go(targetIndex);
          }
        }
      }

      function scrollToGallery() {
        if (!scrollToGalleryOnVariant || !container) return;
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      var variantChangeTimer = null;
      function handleVariantMediaChange(data) {
        if (!data || data.sectionId !== sectionId) return;
        var variant = data.variant;
        if (!variant) return;

        if (variantChangeTimer) {
          clearTimeout(variantChangeTimer);
        }
        variantChangeTimer = setTimeout(function () {
          filterSlidesByVariant(variant);
          // A block restating the page's initial variant is not a change: the gallery starts
          // where "Show variant image on page load" put it.
          if (!data.initial) {
            navigateToVariantMedia(variant);
            scrollToGallery();
          }
          variantChangeTimer = null;
        }, 50);
      }

      var variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        handleVariantMediaChange(event.data);
      });

      // An offer bundle's later slots pick a variant without changing the main one; only the gallery follows them.
      function onOfferSlotVariantChange(event) {
        handleVariantMediaChange(event.detail);
      }
      document.addEventListener('offers:slot-variant-change', onOfferSlotVariantChange);

      galleryEl.variantChangeUnsubscriber = function () {
        variantChangeUnsubscriber();
        document.removeEventListener('offers:slot-variant-change', onOfferSlotVariantChange);
      };
    })();

    // Lightbox
    if (ds.imageZoom === 'lightbox') {
      (function () {
        var lightbox = document.getElementById('ProductMediaLightbox-' + sectionId);
        if (!lightbox || lightbox.__pmgLightboxBound) return;
        lightbox.__pmgLightboxBound = true;

        var lightboxImg = lightbox.querySelector('.product-media-lightbox__image:not(.product-media-lightbox__image--side)');
        var stage = lightbox.querySelector('.product-media-lightbox__stage');
        var sideImgs = {
          prev: lightbox.querySelector('.product-media-lightbox__image--side[data-side="prev"]'),
          next: lightbox.querySelector('.product-media-lightbox__image--side[data-side="next"]'),
        };
        var closeBtn = lightbox.querySelector('.product-media-lightbox__close');
        var prevBtn = lightbox.querySelector('.product-media-lightbox__nav--prev');
        var nextBtn = lightbox.querySelector('.product-media-lightbox__nav--next');
        var lightboxThumbs = lightbox.querySelectorAll('.product-media-lightbox__thumb');
        var lightboxOriginalParent = lightbox.parentNode;
        var currentIndex = 0;
        var mediaSrcs = [];
        var mediaIds = [];
        var mediaSlides = [];
        var swipeMoved = false;
        var sliding = false;
        var neighborsArmed = false;

        // The lightbox holds the pictures the gallery shows right now, read again each time it
        // opens: a variant change rebuilds the slides, and a looping gallery's copies are skipped.
        // Its thumbnails follow by media id; the ones for pictures not shown are hidden.
        function readGallery() {
          mediaSrcs = [];
          mediaIds = [];
          mediaSlides = [];
          galleryEl
            .querySelectorAll('.splide__list > .splide__slide:not(.splide__slide--clone)')
            .forEach(function (slide, slideIndex) {
              var img = slide.querySelector('.product-media-gallery__image');
              if (!img) return;
              var src = img.src || img.currentSrc;
              mediaSrcs.push(src.replace(/width=\d+/, 'width=2000'));
              mediaIds.push(slide.dataset.mediaId);
              mediaSlides.push(slideIndex);
            });
          lightboxThumbs.forEach(function (thumb) {
            var idx = mediaIds.indexOf(thumb.dataset.mediaId);
            thumb.hidden = idx === -1;
            thumb.dataset.index = idx;
          });
        }
        readGallery();

        function syncGallerySlide() {
          var splide = galleryEl.splide;
          if (splide && mediaSlides[currentIndex] !== undefined) splide.go(mediaSlides[currentIndex]);
        }

        function updateLightboxThumbs() {
          lightboxThumbs.forEach(function (thumb) {
            thumb.classList.toggle('is-active', parseInt(thumb.dataset.index, 10) === currentIndex);
          });
        }

        function syncSideImages() {
          if (!neighborsArmed) return;
          ['prev', 'next'].forEach(function (side) {
            var el = sideImgs[side];
            if (!el) return;
            var idx = currentIndex + (side === 'prev' ? -1 : 1);
            if (idx < 0 || idx > mediaSrcs.length - 1) {
              el.removeAttribute('src');
            } else if (el.getAttribute('src') !== mediaSrcs[idx]) {
              el.src = mediaSrcs[idx];
            }
          });
        }

        function armNeighbors() {
          if (neighborsArmed) return;
          neighborsArmed = true;
          syncSideImages();
        }

        function sideImageReady(index) {
          var el = sideImgs[index > currentIndex ? 'next' : 'prev'];
          return !!el && el.getAttribute('src') === mediaSrcs[index] && el.complete && el.naturalWidth > 0;
        }

        function syncLightboxChrome() {
          prevBtn.style.display = currentIndex === 0 ? 'none' : 'flex';
          nextBtn.style.display = currentIndex === mediaSrcs.length - 1 ? 'none' : 'flex';
          updateLightboxThumbs();
          syncSideImages();
        }

        function showLightboxImage() {
          lightboxImg.src = mediaSrcs[currentIndex];
          syncLightboxChrome();
        }

        function moveStage(offset) {
          if (!stage) return;
          void stage.offsetWidth;
          stage.style.transition = '';
          stage.style.transform = offset;
        }

        function goToLightboxImage(index) {
          if (sliding) return;
          if (index < 0 || index > mediaSrcs.length - 1 || index === currentIndex) return;

          var oneStep = Math.abs(index - currentIndex) === 1;
          var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          armNeighbors();
          if (!stage || !oneStep || reduceMotion || !sideImageReady(index)) {
            moveStage('translateX(0)');
            currentIndex = index;
            showLightboxImage();
            syncGallerySlide();
            return;
          }

          sliding = true;
          var landed = false;
          var travelTimer;

          function land(e) {
            if (e && (e.target !== stage || e.propertyName !== 'transform')) return;
            if (landed) return;
            landed = true;
            stage.removeEventListener('transitionend', land);
            clearTimeout(travelTimer);
            stage.style.transition = 'none';
            stage.style.transform = 'translateX(0)';
            currentIndex = index;
            showLightboxImage();
            syncGallerySlide();
            void stage.offsetWidth;
            stage.style.transition = '';
            sliding = false;
          }

          travelTimer = setTimeout(land, 450);
          stage.addEventListener('transitionend', land);
          moveStage('translateX(' + (index > currentIndex ? -100 : 100) + 'vw)');
        }

        function openLightbox(index) {
          currentIndex = index;
          sliding = false;
          if (stage) {
            stage.style.transition = 'none';
            stage.style.transform = 'translateX(0)';
          }
          showLightboxImage();
          document.body.appendChild(lightbox);
          lightbox.classList.add('is-open');
          lightbox.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        }

        function closeLightbox() {
          lightbox.classList.remove('is-open');
          lightbox.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          lightboxOriginalParent.appendChild(lightbox);
        }

        // One listener on the gallery, so slides rebuilt by a variant change open it too.
        galleryEl.addEventListener('click', function (e) {
          var img = e.target.closest && e.target.closest('.product-media-gallery__image');
          var slide = img && img.closest('.splide__slide');
          if (!slide) return;
          readGallery();
          var index = mediaIds.indexOf(slide.dataset.mediaId);
          if (index !== -1) openLightbox(index);
        });

        closeBtn.addEventListener('click', closeLightbox);
        lightbox.addEventListener('click', function (e) {
          if (swipeMoved) {
            swipeMoved = false;
            return;
          }
          if (e.target === lightbox) closeLightbox();
        });

        prevBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          goToLightboxImage(currentIndex - 1);
        });
        nextBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          goToLightboxImage(currentIndex + 1);
        });

        lightboxThumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function (e) {
            e.stopPropagation();
            goToLightboxImage(parseInt(thumb.dataset.index, 10));
          });
        });

        var swipeStartX = 0;
        var swipeStartY = 0;
        var swipeDX = 0;
        var swipeAxis = null;
        var swiping = false;

        lightbox.addEventListener(
          'touchstart',
          function (e) {
            armNeighbors();
            if (sliding) {
              swipeMoved = true;
              return;
            }
            swipeMoved = false;
            if (!stage || e.touches.length !== 1) return;
            if (
              e.target.closest(
                '.product-media-lightbox__thumbs, .product-media-lightbox__close, .product-media-lightbox__nav'
              )
            ) {
              return;
            }
            swiping = true;
            swipeAxis = null;
            swipeDX = 0;
            swipeStartX = e.touches[0].clientX;
            swipeStartY = e.touches[0].clientY;
            stage.style.transition = 'none';
          },
          { passive: true }
        );

        lightbox.addEventListener(
          'touchmove',
          function (e) {
            if (!swiping || sliding || e.touches.length !== 1) return;
            var dx = e.touches[0].clientX - swipeStartX;
            var dy = e.touches[0].clientY - swipeStartY;
            if (swipeAxis === null) {
              if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
              swipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
              if (swipeAxis === 'y') {
                swiping = false;
                moveStage('translateX(0)');
                return;
              }
              swipeMoved = true;
            }
            var pastEdge = (dx > 0 && currentIndex === 0) || (dx < 0 && currentIndex === mediaSrcs.length - 1);
            swipeDX = pastEdge ? dx * 0.25 : dx;
            stage.style.transform = 'translateX(' + swipeDX + 'px)';
          },
          { passive: true }
        );

        function endSwipe() {
          if (!swiping) return;
          swiping = false;
          var threshold = Math.max(40, lightbox.clientWidth * 0.12);
          var target = currentIndex;
          if (swipeDX <= -threshold) {
            target = currentIndex + 1;
          } else if (swipeDX >= threshold) {
            target = currentIndex - 1;
          }
          if (target !== currentIndex && target >= 0 && target <= mediaSrcs.length - 1) {
            goToLightboxImage(target);
          } else {
            moveStage('translateX(0)');
          }
        }

        lightbox.addEventListener('touchend', endSwipe);
        lightbox.addEventListener('touchcancel', endSwipe);

        lightbox.addEventListener('pointerover', armNeighbors);

        document.addEventListener('keydown', function (e) {
          if (!lightbox.classList.contains('is-open')) return;
          armNeighbors();
          if (e.key === 'Escape') closeLightbox();
          if (e.key === 'ArrowLeft') goToLightboxImage(currentIndex - 1);
          if (e.key === 'ArrowRight') goToLightboxImage(currentIndex + 1);
        });
      })();
    }

    // Force autoplay for the active slide's video (Safari needs manual play())
    if (ds.videoAutoplay === 'true') {
      var playVisibleVideos = function () {
        var activeSlide = galleryEl.querySelector('.splide__slide.is-active');
        galleryEl.querySelectorAll('.product-media-gallery__video').forEach(function (video) {
          var slide = video.closest('.splide__slide');
          if (slide && slide === activeSlide) {
            video.muted = true;
            video.play().catch(function () {
              video.addEventListener('canplay', function retryPlay() {
                video.removeEventListener('canplay', retryPlay);
                if (video.closest('.splide__slide.is-active')) {
                  video.play().catch(function () {});
                }
              });
            });
          } else {
            video.pause();
          }
        });
      };
      playVisibleVideos();
      mainSplide.on('moved', playVisibleVideos);
    }

    // Notify overlay/controls scripts that Splide was (re)initialized
    galleryEl.dispatchEvent(new CustomEvent('gallery:init'));
  }

  function initAll(root) {
    (root || document).querySelectorAll('.product-media-gallery').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Reinitialize on width changes only (mobile scroll fires height-only resizes)
  var resizeTimer;
  var lastResizeWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    var newWidth = window.innerWidth;
    if (newWidth === lastResizeWidth) return;
    lastResizeWidth = newWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      initAll();
    }, 250);
  });
})();
